import {
  closestToLine,
  isEmptyBullets,
  LINE_MARKER,
  locateQuote,
  suggestionText,
  type TextRange,
} from '../content/copyEditQuote';
import type { Finding } from './shared';

/**
 * Where a copy-edit finding's words are in the page editor (backlog f2261d58).
 * A block's text can sit in a field, or in a block that has no fields here
 * (features, questions, tables): both can take the fix. A heading or text field
 * holds *stars* and [links](…), which the quote read as plain words.
 */

type Block = { type: string; [key: string]: any };
export interface EditedPage {
  title: string;
  description: string;
  blocks: Block[];
}
type Path = Array<string | number>;

export interface PageSpot extends TextRange {
  kind: 'title' | 'description' | 'block';
  block: number | null;
  path: Path;
  /** It has a field in the form, rather than only the block's card. */
  field: boolean;
}

/** The block's settings and addresses: words a reader doesn't read. */
const NOT_WORDS =
  /^(_.*|type|style|variant|layout|icon|cell|key|size|color|.*(href|src|url|id|slug))$/i;

const MARKS = String.raw`(?:\*{1,2}|\[|\]\([^)\s]*\))*`;

const looksLikeHtml = (s: string) => /<[a-z/][^>]*>|&[a-z#][a-z0-9]*;/i.test(s);

/** A quote in a field's text, looking through its marks. */
export function locateMarked(text: string, quote: string): TextRange | null {
  const plain = locateQuote(text, quote);
  if (plain) return plain;
  const words = quote
    .trim()
    .replace(LINE_MARKER, '')
    .replace(/[\s ]+/g, ' ');
  if (!words) return null;
  const pattern = Array.from(words)
    .map((c) => (c === ' ' ? '[\\s\\u00a0]+' : c.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&')))
    .join(MARKS);
  const hit = new RegExp(pattern, 'u').exec(text);
  return hit ? { from: hit.index, to: hit.index + hit[0].length } : null;
}

/** Every string a reader can read in a block, with where it is. */
function wordsIn(value: unknown, path: Path = []): Array<{ path: Path; text: string }> {
  if (typeof value === 'string') return value ? [{ path, text: value }] : [];
  if (Array.isArray(value)) return value.flatMap((v, i) => wordsIn(v, [...path, i]));
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([k, v]) =>
      NOT_WORDS.test(k) ? [] : wordsIn(v, [...path, k])
    );
  }
  return [];
}

/** The form's fields, block by block (BlockFields). */
function hasField(b: Block, path: Path) {
  const key = path.join('.');
  switch (b.type) {
    case 'hero':
      return key === 'heading' || key === 'text';
    case 'articles':
      return key === 'heading';
    case 'text':
      return key === 'heading' || (key === 'body' && !b._rich);
    case 'quote':
      return key === 'text' || key === 'attribution';
    case 'cta':
      return key === 'heading' || key === 'button.label';
    default:
      return false;
  }
}

const inHtml = (s: string, quote: string) =>
  looksLikeHtml(s) ? locateQuote(s, quote) : locateMarked(s, quote);

function spotInBlock(b: Block, index: number, f: Finding): PageSpot | null {
  const quote = f.quote || '';
  const spots: Array<PageSpot & { text: string }> = [];
  wordsIn(b).forEach(({ path, text }) => {
    const field = hasField(b, path);
    const hit = field ? locateMarked(text, quote) : inHtml(text, quote);
    if (hit) spots.push({ ...hit, kind: 'block', block: index, path, field, text });
  });
  return closestToLine(spots, (s) => s.text, f.line);
}

/** Where the finding's words are now, or null when they can't be changed here. */
export function locatePageFinding(page: EditedPage, f: Finding): PageSpot | null {
  const quote = f.quote || '';
  if (!quote) return null;
  // An empty bullet isn't text, and punctuation with no line to place it is a guess.
  if (isEmptyBullets(f) || (!/[\p{L}\p{N}]/u.test(quote) && !f.line)) return null;
  if (f.path === 'title' || f.path === 'seo.metaDescription') {
    const title = f.path === 'title';
    const hit = locateQuote(title ? page.title : page.description, quote);
    return hit
      ? {
          ...hit,
          kind: title ? 'title' : 'description',
          block: null,
          path: [title ? 'title' : 'description'],
          field: true,
        }
      : null;
  }
  // The form has no field for these, and the page's blocks don't hold them.
  if (f.path === 'summary' || f.path === 'seo.metaTitle') return null;
  // Its own block first: a block may have moved since the check, or say the same words.
  const said = /^blocks\[(\d+)\]/.exec(f.path || '');
  const first = said ? Number(said[1]) : -1;
  const own = page.blocks[first] ? spotInBlock(page.blocks[first], first, f) : null;
  if (own) return own;
  for (let i = 0; i < page.blocks.length; i += 1) {
    const hit = i === first ? null : spotInBlock(page.blocks[i], i, f);
    if (hit) return hit;
  }
  return null;
}

function setAt<T>(value: T, path: Path, next: string): T {
  if (!path.length) return next as unknown as T;
  const [head, ...rest] = path;
  const copy: any = Array.isArray(value) ? [...value] : { ...value };
  copy[head] = setAt(copy[head], rest, next);
  return copy;
}

const asHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The page with the suggestion in place of the finding's words, or null if they're gone. */
export function applyPageFinding<P extends EditedPage>(page: P, f: Finding): P | null {
  const spot = locatePageFinding(page, f);
  const suggestion = suggestionText(f);
  if (!spot || suggestion === null) return null;
  const swap = (s: string, html: boolean) =>
    s.slice(0, spot.from) + (html ? asHtml(suggestion) : suggestion) + s.slice(spot.to);
  if (spot.kind === 'title') return { ...page, title: swap(page.title, false) };
  if (spot.kind === 'description') return { ...page, description: swap(page.description, false) };
  const block = page.blocks[spot.block as number];
  const text = spot.path.reduce((v: any, k) => v[k], block) as string;
  const html = !spot.field && looksLikeHtml(text);
  return {
    ...page,
    blocks: page.blocks.map((b, i) =>
      i === spot.block ? setAt(b, spot.path, swap(text, html)) : b
    ),
  };
}

/** A string as a reader reads it: no tags, entities or marks. */
function readerWords(s: string) {
  const text = /[<&]/.test(s)
    ? new DOMParser().parseFromString(s, 'text/html').body.textContent || ''
    : s;
  return text
    .replace(/\]\([^)\s]*\)/g, '')
    .replace(/[*[]/g, '')
    .replace(/[\s ]+/g, ' ');
}

/** Is this finding about words still on the page? An applied one no longer is. */
export function stillOnPage(page: EditedPage, f: Finding) {
  if (f.source !== 'copy_edit' || !f.quote) return true;
  if (f.path === 'summary' || f.path === 'seo.metaTitle') return true;
  const want = readerWords(f.quote.replace(LINE_MARKER, '')).trim();
  if (f.path === 'title') return readerWords(page.title).includes(want);
  if (f.path === 'seo.metaDescription') return readerWords(page.description).includes(want);
  return wordsIn(page.blocks).some(({ text }) => readerWords(text).includes(want));
}
